// src/services/contractAccessService.js - UPDATED VERSION
import api from './api';

export const contractAccessService = {
  /**
   * Validează token-ul și returnează informații despre contract
   * @param {string} token - Access token
   * @param {number} contractId - (Optional) ID-ul contractului specific de returnat
   */
  async validateToken(token, contractId = null) {
    const url = contractId
      ? `/contract-access/${token}?contractId=${contractId}`
      : `/contract-access/${token}`;

    const response = await api.get(url);
    return response.data;
  },

  // Verifică codul de acces
  async verifyCode(token, code) {
    const response = await api.post(`/contract-access/${token}/verify-code`, code);
    return response.data;
  },

  // Descarcă contractul ca blob
  async downloadContract(token) {
    const response = await api.get(`/contract-access/${token}/download`, {
      responseType: 'blob',
    });
    return response.data; // This should be a blob
  },

  /**
   * Încarcă un singur document semnat (LEGACY METHOD)
   * @param {string} token - Access token
   * @param {File} file - Single file to upload
   */
  async uploadSignedDocument(token, file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`/contract-access/${token}/upload-signed-single`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },


  /**
   * Încarcă multiple documente semnate (NEW METHOD)
   * @param {string} token - Access token
   * @param {File[]} files - Array of files to upload
   */
  async uploadSignedDocuments(token, files) {
    const formData = new FormData();

    // Log pentru debugging
    console.log('Multi-file upload starting:', {
      token,
      fileCount: files.length,
      files: files.map(f => ({
        name: f.name,
        size: f.size,
        type: f.type
      }))
    });

    // CRITICAL: Append each file individually with the SAME key 'files'
    // This is what ASP.NET Core expects for List<IFormFile>
    files.forEach((file, index) => {
      formData.append('files', file);
      console.log(`Appended file ${index + 1}:`, file.name);
    });

    // Log FormData contents for debugging
    console.log('FormData entries:');
    for (let pair of formData.entries()) {
      console.log(pair[0], pair[1]);
    }

    try {
      const response = await api.post(`/contract-access/${token}/upload-signed`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Upload successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('Upload failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  /**
 * Obține lista documentelor semnate încărcate
 * @param {string} token - Access token
 */
  async getSignedDocuments(token) {
    const response = await api.get(`/contract-access/${token}/signed-documents`);
    return response.data;
  },

  // ============ ADMIN METHODS ============

  // Generează cod de acces pentru contract
  async generateAccess(contractId, validityHours = 48) {
    const response = await api.post(`/contracts/${contractId}/generate-access`, {
      validityHours
    });
    return response.data;
  },

  // Obține informații despre accesul unui contract
  async getAccessInfo(contractId) {
    const response = await api.get(`/contracts/${contractId}/access-info`);
    return response.data;
  },

  // Trimite email cu codul de acces
  async sendAccessEmail(contractId, emails = []) {
    const response = await api.post(`/contracts/${contractId}/send-access-email`, { emails } );
    return response.data;
  },

  // Metode adiționale - pentru compatibilitate cu codul existent
  async getContractByToken(token, contractId = null) {
    return await this.validateToken(token, contractId);
  },

  async verifyAccessCode(token, code) {
    return await this.verifyCode(token, code);
  },

  // ============ ADDITIONAL ACTS METHODS (✨ NOU) ============

  /**
   * Descarcă PDF-ul unui act adițional
   * @param {string} token - Access token
   * @param {number} actId - ID-ul actului adițional
   */
  async downloadAdditionalAct(token, actId) {
    const response = await api.get(
      `/contract-access/${token}/additional-act/${actId}/download`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  /**
   * Upload documente semnate pentru un act adițional
   * @param {string} token - Access token
   * @param {number} actId - ID-ul actului adițional
   * @param {File[]} files - Array of files to upload
   */
  async uploadAdditionalActDocuments(token, actId, files) {
    const formData = new FormData();

    files.forEach((file, index) => {
      formData.append('files', file);
      console.log(`Appended file ${index + 1} for act ${actId}:`, file.name);
    });

    try {
      const response = await api.post(
        `/contract-access/${token}/additional-act/${actId}/upload-signed`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log('Act upload successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('Act upload failed:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  /**
   * Obține lista documentelor semnate pentru un act adițional
   * @param {string} token - Access token
   * @param {number} actId - ID-ul actului adițional
   */
  async getAdditionalActSignedDocuments(token, actId) {
    const response = await api.get(
      `/contract-access/${token}/additional-act/${actId}/signed-documents`
    );
    return response.data;
  },



  // ============ TERMINATION NOTICES METHODS (✨ NOU) ============

  /**
   * Descarcă PDF-ul unei notificări de reziliere
   * @param {string} token - Access token
   * @param {number} noticeId - ID-ul notificării de reziliere
   */
  async downloadNotice(token, noticeId) {
    const response = await api.get(
      `/contract-access/${token}/notice/${noticeId}/download`, // ✨ Endpoint nou
      { responseType: 'blob' }
    );
    return response.data;
  },

  /**
   * Upload documente semnate pentru o notificare de reziliere
   * @param {string} token - Access token
   * @param {number} noticeId - ID-ul notificării de reziliere
   * @param {File[]} files - Array of files to upload
   */
  async uploadNoticeDocuments(token, noticeId, files) {
    const formData = new FormData();

    files.forEach((file, index) => {
      formData.append('files', file);
      console.log(`Appended file ${index + 1} for notice ${noticeId}:`, file.name);
    });

    try {
      const response = await api.post(
        `/contract-access/${token}/notice/${noticeId}/upload-signed`, // ✨ Endpoint nou
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log('Notice upload successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('Notice upload failed:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  /**
   * Obține lista documentelor semnate pentru o notificare de reziliere
   * @param {string} token - Access token
   * @param {number} noticeId - ID-ul notificării de reziliere
   */
  async getNoticeSignedDocuments(token, noticeId) {
    const response = await api.get(
      `/contract-access/${token}/notice/${noticeId}/signed-documents` // ✨ Endpoint nou
    );
    return response.data;
  },

  /**
 * Obține toate contractele clientului după autentificare
 * @param {string} token - Access token
 * @returns {Promise} - Lista contractelor clientului grupate cu detalii
 */
  async getClientContracts(token) {
    try {
      const response = await api.get(`/contract-access/${token}/client-contracts`);
      console.log('Client contracts loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to load client contracts:', error);
      throw error;
    }
  }
};